import {
  ActionTakenType,
  DocumentTemplateType,
  PunchListStatusType,
  WorkflowStatusType,
} from '../api-client/autogenerated';

/**
 * Frontend mirror of backend `tsoa-src/documents/punch-list-status-utils.ts`. See that file for the
 * full reasoning; the two packages share no code, so this is a hand-kept copy. Keep the table
 * identical to the backend's.
 *
 *   status                  | workflowStatus                  | actionTaken        | label
 *   ------------------------|---------------------------------|--------------------|-----------------------
 *   new                     | Initial                         | --                 | New
 *   in_progress             | ReadyForSubmissionToArchitect   | --                 | In Progress
 *   ready_for_verification  | SubmittedForReview              | --                 | Ready for Verification
 *                           |   ...or UnderReview             | --                 | Ready for Verification
 *   accepted                | Completed                       | NoExceptionsTaken  | Accepted
 *   rejected                | Completed                       | ReviseAndResubmit  | Rejected
 *
 * The order matches the workflow's own: a punch item advances Initial -> ReadyForSubmissionToArchitect
 * -> SubmittedForReview/UnderReview -> Completed, and it is NOT workflow-inverted. That is what
 * getCurrentlyResponsibleUsers in utils.ts already assumed -- it groups Initial +
 * ReadyForSubmissionToArchitect as the contractor's phase and SubmittedForReview + UnderReview as
 * the architect's -- and why Punch List appears in neither AddDocumentDialog's nor
 * EditDocumentsDialog's list of inverted types.
 *
 * Warranty Items is deliberately NOT part of this. It keeps punchListStatus as its only status.
 */
export interface PunchListWorkflowState {
  workflowStatus: WorkflowStatusType;
  actionTaken: ActionTakenType | null;
}

const PUNCH_LIST_STATUS_TO_WORKFLOW: Record<PunchListStatusType, PunchListWorkflowState> = {
  [PunchListStatusType.New]: {
    workflowStatus: WorkflowStatusType.Initial,
    actionTaken: null,
  },
  [PunchListStatusType.InProgress]: {
    workflowStatus: WorkflowStatusType.ReadyForSubmissionToArchitect,
    actionTaken: null,
  },
  [PunchListStatusType.ReadyForVerification]: {
    workflowStatus: WorkflowStatusType.SubmittedForReview,
    actionTaken: null,
  },
  [PunchListStatusType.Accepted]: {
    workflowStatus: WorkflowStatusType.Completed,
    actionTaken: ActionTakenType.NoExceptionsTaken,
  },
  [PunchListStatusType.Rejected]: {
    workflowStatus: WorkflowStatusType.Completed,
    actionTaken: ActionTakenType.ReviseAndResubmit,
  },
};

/**
 * Read-only states: UnderReview is reached from SubmittedForReview when the architect opens an item
 * awaiting verification. Both are the architect's phase and both are Ready for Verification.
 */
const ADDITIONAL_READ_STATES: [PunchListWorkflowState, PunchListStatusType][] = [
  [
    { workflowStatus: WorkflowStatusType.UnderReview, actionTaken: null },
    PunchListStatusType.ReadyForVerification,
  ],
];

const stateKey = ({ workflowStatus, actionTaken }: PunchListWorkflowState) =>
  `${workflowStatus}|${actionTaken ?? ''}`;

const WORKFLOW_TO_PUNCH_LIST_STATUS: ReadonlyMap<string, PunchListStatusType> = new Map([
  ...(Object.entries(PUNCH_LIST_STATUS_TO_WORKFLOW) as [
    PunchListStatusType,
    PunchListWorkflowState,
  ][]).map(([status, state]): [string, PunchListStatusType] => [stateKey(state), status]),
  ...ADDITIONAL_READ_STATES.map(([state, status]): [string, PunchListStatusType] => [
    stateKey(state),
    status,
  ]),
]);

/**
 * Punch List only. Named for the document type rather than "punch list types" on purpose: Warranty
 * Items shares punchListStatus and its transition rules but NOT this mapping.
 */
export const isPunchListDocument = (documentType: DocumentTemplateType | null | undefined) =>
  documentType === DocumentTemplateType.PunchList;

/** Write side: the workflow fields a punch list status corresponds to. */
export const punchListStatusToWorkflow = (status: PunchListStatusType): PunchListWorkflowState =>
  PUNCH_LIST_STATUS_TO_WORKFLOW[status];

/**
 * Read side: the punch list status a document's workflow fields correspond to, or undefined when
 * they correspond to none of them (a withdrawn item is `Completed` + `Withdrawn`, which is neither
 * accepted nor rejected).
 *
 * Ask this only about Punch List documents — the workflow fields alone cannot say what kind of
 * document they belong to, so every `Initial` submittal would otherwise answer "new".
 */
export const getPunchListStatus = (document: {
  workflowStatus?: WorkflowStatusType | null;
  actionTaken?: ActionTakenType | null;
}): PunchListStatusType | undefined => {
  if (!document.workflowStatus) return undefined;
  return WORKFLOW_TO_PUNCH_LIST_STATUS.get(
    stateKey({
      workflowStatus: document.workflowStatus,
      actionTaken: document.actionTaken ?? null,
    }),
  );
};
